import { useAuth0 } from "@auth0/auth0-react";
import { useMemo } from "react";

interface GreenPassMember {
  externalSystemId: string;
  type: "greenpass";
}

interface HealthMember {
  contactId: string;
  customerNumber: string;
  type: "health";
  status: "active" | "suspended" | "terminated";
}

interface NibAppUser {
  "https://nib.com.au:personId": string;
  "https://nib.com.au:identities": {
    members: (GreenPassMember | HealthMember)[];
  };
}

interface GuAppUser {
  "https://guhealth.com.au:personId": string;
  "https://guhealth.com.au:identities": {
    members: HealthMember[];
  };
}

export const useAppAuth = () => {
  const { user, ...rest } = useAuth0<NibAppUser | GuAppUser>();

  const personId = useMemo(() => {
    if (user === undefined) {
      return undefined;
    }

    if ("https://nib.com.au:personId" in user) {
      return user["https://nib.com.au:personId"];
    }

    return user["https://guhealth.com.au:personId"];
  }, [user]);

  const member = useMemo(() => {
    if (user === undefined) {
      return undefined;
    }

    const identities =
      "https://nib.com.au:identities" in user
        ? user["https://nib.com.au:identities"]
        : user["https://guhealth.com.au:identities"];

    const healthMember = identities.members.find(
      (member): member is HealthMember => member.type === "health" && member.status === "active"
    );
    if (healthMember) {
      return healthMember;
    }

    const greenPassMember = identities.members.find(
      (member): member is GreenPassMember => member.type === "greenpass"
    );
    return greenPassMember;
  }, [user]);

  return {
    ...rest,
    user,
    personId,
    member,
  };
};
